import { useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { getDispatchReportRange } from "@/api/report/getDispatchReportRange";
import { ReportView } from "@/features/report/ReportView";

const COLUMNS: GridColDef[] = [
  { field: "consignmentNumber", headerName: "Consignment #", width: 150 },
  { field: "item", headerName: "Item", width: 160 },
  { field: "date", headerName: "Date", width: 110 },
  { field: "orderReference", headerName: "Order Ref", width: 140 },
  { field: "quantity", headerName: "Qty", type: "number", width: 80 },
  { field: "carrier", headerName: "Carrier", width: 150 },
  { field: "trackingNumber", headerName: "Tracking #", width: 200 },
  {
    field: "trackingUrl",
    headerName: "Track",
    width: 90,
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      const url = params.value as string | null;
      if (!url) {
        return null;
      }
      return (
        <Link
          href={url}
          target="_blank"
          rel="noopener"
        >
          link
        </Link>
      );
    },
  },
  { field: "sourceFile", headerName: "Source file", flex: 1, minWidth: 260 },
];

/** Today's local calendar date (YYYY-MM-DD). */
const todayLocal = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Dispatch report page: every dispatch report on the SFTP in a from/to range (by modify time), merged, each row tagged with its source file. */
export const DispatchReport = () => {
  const [from, setFrom] = useState(todayLocal());
  const [to, setTo] = useState(todayLocal());

  const query = getDispatchReportRange.useQuery(
    { from, to },
    { skip: from === "" || to === "" },
  );
  const rows = query.data ?? null;

  let error: string | null = null;
  if (query.isError) {
    error = JSON.stringify(query.error);
  }

  const gridRows = (rows ?? []).map((row, i) => ({
    id: i,
    consignmentNumber: row.consignmentNumber,
    item: row.item,
    date: row.date,
    orderReference: row.orderReference,
    quantity: row.quantity,
    carrier: row.tracking.carrier,
    trackingNumber: row.tracking.number,
    trackingUrl: row.tracking.url,
    sourceFile: row.sourceFile,
  }));

  const dateRange = (
    <Box
      sx={{
        display: "flex",
        gap: 2,
      }}
    >
      <TextField
        label="From"
        type="date"
        size="small"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label="To"
        type="date"
        size="small"
        value={to}
        onChange={(e) => setTo(e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Box>
  );

  return (
    <ReportView
      title="Dispatch report"
      loading={query.isFetching}
      error={error}
      count={rows?.length ?? null}
      action={dateRange}
    >
      {rows && (
        <Box sx={{ height: "calc(100vh - 220px)", width: "100%" }}>
          <DataGrid
            rows={gridRows}
            columns={COLUMNS}
            density="compact"
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </ReportView>
  );
};
